import { isMobileOnly } from 'react-device-detect';
import { FeatureFlag } from './feature-flags/FeatureFlagsProvider';

/**
 * Determine if a `childPath` is in `parentPath`.
 * E.g. child: /dic/file/a/123.html, parent: /dict/file/ should return True
 * child: /dic/file/a/123.html, parent: /dict/file should return True
 * child: /dic/file_folder/a/123.html, parent: /dict/file should return False
 *
 * @param {*} childPath
 * @param {*} parentPath
 * @returns
 */
const isPathInside = (childPath: string, parentPath: string) => {
  const normalizedParentPath =
    parentPath[parentPath.length - 1] === '/' ? parentPath : `${parentPath}/`;
  return childPath.startsWith(normalizedParentPath) && childPath.startsWith(normalizedParentPath);
};

type RoleTypes =
  | 'owner'
  | 'admin'
  | 'adminManager'
  | 'engineer'
  | 'pilot'
  | 'user'
  | 'viewOnly'
  | 'subContractor'
  | 'reportViewer'
  | 'crewScheduleViewer'
  | 'taggedScheduleViewer'
  | 'reviewer'
  | 'manager'
  | 'trackedItemsAdmin'
  | 'trackedItemsViewer'
  | 'companyLibraryEditor'
  | 'companyLibraryViewer'
  | 'customer'
  | 'ipadLoginOnly';

export const getRedirectPath = (
  isLoginLayout: boolean,
  isUserAuthenticated: boolean,
  featureFlags: FeatureFlag,
  settings: {
    CrewSchedulingOnly: boolean;
    CrewSchedulingV1: boolean;
    IntegrateLoggingWithCrewScheduleV2: boolean;
    ShowDesktopScheduleOnMobile: boolean;
    NonAdminCanViewCrewSchedule: boolean;
    ShowAircraftPage: boolean;
    PublishedSchedule: boolean;
  },
  roles: RoleTypes[],
) => {
  // redirect to login page if current is not login page and user not authenticated
  if (!isLoginLayout && !isUserAuthenticated) {
    return '/user/login';
  }

  // direct to appropriate page for the role if the user is on login page and authenticated or not on login page
  if (isUserAuthenticated) {
    if (roles.includes('admin') || roles.includes('viewOnly') || roles.includes('subContractor')) {
      if (settings?.CrewSchedulingOnly && settings?.CrewSchedulingV1) {
        return '/data/organization/crew-scheduling';
      }

      if (settings?.PublishedSchedule) {
        return '/data/organization/published-schedule';
      }

      if (settings?.CrewSchedulingOnly && !settings?.CrewSchedulingV1) {
        if (isMobileOnly && featureFlags && featureFlags[FeatureFlag.CrewScheduleMobile]) {
          if (isLoginLayout) {
            return '/data/organization/crew-scheduling-mobile';
          }
          if (settings.ShowDesktopScheduleOnMobile) {
            return '/data/organization/crew-scheduling-v3';
          }
        }
        if (isMobileOnly && featureFlags && !featureFlags[FeatureFlag.CrewScheduleMobile]) {
          return '/data/organization/crew-scheduling-v3';
        }
        return '/data/organization/crew-scheduling-v3';
      }

      return '/data/organization/fleet-overview';
    } else if (roles.includes('engineer') && settings?.ShowAircraftPage) {
      return '/data/organization/aircraft';
    } else if (roles.includes('engineer') && !settings?.ShowAircraftPage) {
      return '/data/organization/fleet-overview';
    } else if (roles.includes('customer')) {
      return '/data/organization/fleet-overview';
    } else if (
      roles.includes('pilot') ||
      roles.includes('crewScheduleViewer') ||
      roles.includes('taggedScheduleViewer')
    ) {
      // show flight and duty page if the crew schedule page is not visible
      if (
        (settings?.CrewSchedulingOnly && !settings?.NonAdminCanViewCrewSchedule) ||
        !settings?.CrewSchedulingOnly
      ) {
        return '/profile/duty';
      }

      if (settings?.PublishedSchedule) {
        return '/data/organization/published-schedule';
      }

      if (isMobileOnly && isLoginLayout) {
        return '/data/organization/crew-scheduling-mobile';
      }
      return '/data/organization/crew-scheduling-v3';
    } else if (roles.includes('reportViewer')) {
      return '/data/organization/reports';
    } else if (roles.includes('user') || roles.includes('ipadLoginOnly')) {
      return '/profile/manage';
    }
  }

  return null;
};

/**
 * Return the `path` that's inside `paths` array. If there's no exact match in `paths` for `path` treat
 * `path` as a child path and try to match it with a parent path in `paths`.
 * For example '/data/users/123' would match `/data/users` if that `paths` contains `/data/users` as an element
 * @param {*} path String Path or child path
 * @param {*} paths String Array of paths
 * @return false | string
 */
const pathInPaths = (path: string, paths: string[]) => {
  for (let i = 0; i < paths.length; i += 1) {
    if (path === paths[i]) {
      return paths[i];
    }

    if (isPathInside(path, paths[i])) {
      return paths[i];
    }
    // eslint-disable-next-line no-continue
    continue;
  }

  return false;
};

export const isPathAllowedForRole = (
  roles: RoleTypes[],
  pathname: string,
  settings: {
    CrewSchedulingOnly?: boolean;
    AerochexEnabled?: boolean;
    CrewSchedulingV1?: boolean;
    IntegrateLoggingWithCrewScheduleV2?: boolean;
    NonAdminCanViewCrewSchedule?: boolean;
    ShowAircraftPage?: boolean;
    ShowReportsPage?: boolean;
    ShowCrewCompliancePage?: boolean;
    EnableRestAssureIndicator?: boolean;
    CompanyLibrary?: boolean;
    CompanyLibraryAdminOnly?: boolean;
    CompanyLibraryOwnerOnly?: boolean;
    AllowAMEViewCompanyLibrary?: boolean;
    EnableCustomForms?: boolean;
    ManualTrainingItems?: boolean;
    AllowUserPartManagement?: boolean;
    LoggingOnly?: boolean;
  },
) => {
  if (!roles) {
    return false;
  }
  // workaround for metrics dashboard to inherit the legacy dashboard's role/settings gating
  if (pathname === '/data/organization/metrics-dashboard') {
    pathname = '/data/organization/dashboard';
  }
  // The user-linking pages (one per training system) are reached from the
  // integrations tab and are part of company settings, so they inherit that
  // page's gating rather than duplicating the rules across every role map below.
  if (
    pathname === '/data/organization/settings/aerostudies-users' ||
    pathname === '/data/organization/settings/nabu-users'
  ) {
    pathname = '/data/organization/settings';
  }
  /** {@link} src/router.js */
  const rolePathMap: {
    [key in RoleTypes]: {
      [key: string]:
        | boolean
        | {
            CrewSchedulingOnly?: boolean;
            AerochexEnabled?: boolean;
            CrewSchedulingV1?: boolean;
            IntegrateLoggingWithCrewScheduleV2?: boolean;
            NonAdminCanViewCrewSchedule?: boolean;
            ShowAircraftPage?: boolean;
            ShowReportsPage?: boolean;
            ShowCrewCompliancePage?: boolean;
            EnableRestAssureIndicator?: boolean;
            CompanyLibrary?: boolean;
            CompanyLibraryAdminOnly?: boolean;
            CompanyLibraryOwnerOnly?: boolean;
            AllowAMEViewCompanyLibrary?: boolean;
            EnableCustomForms?: boolean;
            EnableLegacyPilotSchedule?: boolean;
            ManualTrainingItems?: boolean;
            AllowUserPartManagement?: boolean;
            LoggingOnly?: boolean;
          }[];
    };
  } = {
    owner: {
      '/data/organization/published-schedule': true,
      '/data/organization/dashboard': [
        // the following will evaluate as
        // (CrewSchedulingOnly == false) || (CrewSchedulingOnly === true && IntegrateLoggingWithCrewScheduleV2 === true)
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/aircraft': true,
      '/data/organization/company-clients': true,
      '/data/organization/invoices': true,
      '/data/organization/sales-dashboard': true,
      '/data/organization/minimums-dashboard': true,
      '/data/organization/air-bills': true,
      '/data/organization/groups': true,
      '/data/aircraft/logs': true,
      '/data/users': true,
      '/data/organization/regulations': [{ CrewSchedulingOnly: true, CrewSchedulingV1: false }],
      '/data/organization/users': true,
      '/data/organization/aircraft': [
        { CrewSchedulingOnly: false, ShowAircraftPage: true },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: true,
        },
      ],
      '/data/organization/tracked-items': [{ ManualTrainingItems: true }],
      '/data/organization/fleet-overview': [
        { CrewSchedulingOnly: false, ShowAircraftPage: false },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: false,
        },
      ],
      '/data/organization/logs': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-scheduling': [{ CrewSchedulingOnly: true, CrewSchedulingV1: true }],
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/duty-times': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-compliance': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, ShowCrewCompliancePage: true },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/aerochex': [{ AerochexEnabled: true }],
      '/data/airports': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/settings': [{ CrewSchedulingOnly: true }, { LoggingOnly: true }],
      '/data/organization/flight-watch': true,
      '/data/organization/pilot-status': [{ EnableRestAssureIndicator: true }],
      '/data/organization/company-library': [{ CompanyLibrary: true }],
      '/data/organization/custom-forms': [{ EnableCustomForms: true }],
      '/data/organization/parts': [{ AllowUserPartManagement: true }],
      '/data/organization/parts/:id': [{ AllowUserPartManagement: true }],
    },
    admin: {
      '/data/organization/dashboard': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/published-schedule': true,
      '/data/organization/bookings': true,
      '/data/organization/groups': true,
      '/data/organization/tracked-items': [{ ManualTrainingItems: true }],
      '/data/aircraft': true,
      '/data/aircraft/logs': true,
      '/data/users': true,
      '/data/organization/regulations': [{ CrewSchedulingOnly: true, CrewSchedulingV1: false }],
      '/data/organization/users': true,
      '/data/organization/aircraft': [
        { CrewSchedulingOnly: false, ShowAircraftPage: true },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: true,
        },
      ],
      '/data/organization/fleet-overview': [
        { CrewSchedulingOnly: false, ShowAircraftPage: false },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: false,
        },
      ],
      '/data/organization/logs': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-scheduling': [{ CrewSchedulingOnly: true, CrewSchedulingV1: true }],
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/duty-times': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-compliance': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, ShowCrewCompliancePage: true },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/aerochex': [{ AerochexEnabled: true }],
      '/data/airports': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/reports': [{ ShowReportsPage: true }],
      '/data/organization/settings': [{ CrewSchedulingOnly: true }, { LoggingOnly: true }],
      '/data/organization/flight-watch': true,
      '/data/organization/pilot-status': [{ EnableRestAssureIndicator: true }],
      '/data/organization/company-library': [
        { CompanyLibrary: true, CompanyLibraryOwnerOnly: false },
      ],
      '/data/organization/custom-forms': [{ EnableCustomForms: true }],
      '/data/organization/parts': [{ AllowUserPartManagement: true }],
      '/data/organization/parts/:id': [{ AllowUserPartManagement: true }],
    },
    adminManager: {
      '/data/organization/dashboard': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/published-schedule': true,
      '/data/organization/groups': true,
      '/data/organization/tracked-items': [{ ManualTrainingItems: true }],
      '/data/aircraft': true,
      '/data/aircraft/logs': true,
      '/data/users': true,
      '/data/organization/regulations': [{ CrewSchedulingOnly: true, CrewSchedulingV1: false }],
      '/data/organization/users': true,
      '/data/organization/aircraft': [
        { CrewSchedulingOnly: false, ShowAircraftPage: true },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: true,
        },
      ],
      '/data/organization/fleet-overview': [
        { CrewSchedulingOnly: false, ShowAircraftPage: false },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: false,
        },
      ],
      '/data/organization/logs': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-scheduling': [{ CrewSchedulingOnly: true, CrewSchedulingV1: true }],
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/duty-times': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-compliance': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, ShowCrewCompliancePage: true },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/aerochex': [{ AerochexEnabled: true }],
      '/data/airports': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/reports': [{ ShowReportsPage: true }],
      '/data/organization/settings': [{ CrewSchedulingOnly: true }, { LoggingOnly: true }],
      '/data/organization/flight-watch': true,
      '/data/organization/pilot-status': [{ EnableRestAssureIndicator: true }],
      '/data/organization/company-library': [
        { CompanyLibrary: true, CompanyLibraryOwnerOnly: false },
      ],
      '/data/organization/custom-forms': [{ EnableCustomForms: true }],
      '/data/organization/parts': [{ AllowUserPartManagement: true }],
      '/data/organization/parts/:id': [{ AllowUserPartManagement: true }],
    },
    manager: {
      '/data/organization/published-schedule': true,
      '/data/organization/groups': true,
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
    },
    reportViewer: {
      '/data/organization/published-schedule': true,
      '/data/organization/dashboard': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/aircraft': true,
      '/data/aircraft/logs': true,
      '/data/organization/aircraft': [
        { CrewSchedulingOnly: false, ShowAircraftPage: true },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: true,
        },
      ],
      '/data/organization/fleet-overview': [
        { CrewSchedulingOnly: false, ShowAircraftPage: false },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: false,
        },
      ],
      '/data/organization/logs': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-scheduling': [{ CrewSchedulingOnly: true, CrewSchedulingV1: true }],
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/duty-times': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-compliance': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, ShowCrewCompliancePage: true },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/aerochex': [{ AerochexEnabled: true }],
      '/data/airports': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/reports': [{ ShowReportsPage: true }],
    },
    // View Only will be able to view the same pages as the admin but not be able to edit them
    viewOnly: {
      '/data/organization/published-schedule': true,
      '/data/organization/dashboard': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/aircraft': true,
      '/data/aircraft/logs': true,
      '/data/users': true,
      '/data/organization/regulations': [{ CrewSchedulingOnly: true, CrewSchedulingV1: false }],
      '/data/organization/users': true,
      '/data/organization/aircraft': [
        { CrewSchedulingOnly: false, ShowAircraftPage: true },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: true,
        },
      ],
      '/data/organization/fleet-overview': [
        { CrewSchedulingOnly: false, ShowAircraftPage: false },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: false,
        },
      ],
      '/data/organization/logs': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-scheduling': [{ CrewSchedulingOnly: true, CrewSchedulingV1: true }],
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/duty-times': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-compliance': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, ShowCrewCompliancePage: true },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/aerochex': [{ AerochexEnabled: true }],
      '/data/airports': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/settings': true,
      '/data/organization/flight-watch': true,
      '/data/organization/pilot-status': [{ EnableRestAssureIndicator: true }],
      '/data/organization/company-library': [
        { CompanyLibrary: true, CompanyLibraryAdminOnly: false, CompanyLibraryOwnerOnly: false },
      ],
      '/data/organization/custom-forms': [{ EnableCustomForms: true }],
    },
    reviewer: {
      '/data/organization/published-schedule': true,
      '/data/organization/dashboard': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/aircraft': true,
      '/data/aircraft/logs': true,
      '/data/users': true,
      '/data/organization/regulations': [{ CrewSchedulingOnly: true, CrewSchedulingV1: false }],
      '/data/organization/users': true,
      '/data/organization/aircraft': [
        { CrewSchedulingOnly: false, ShowAircraftPage: true },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: true,
        },
      ],
      '/data/organization/fleet-overview': [
        { CrewSchedulingOnly: false, ShowAircraftPage: false },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: false,
        },
      ],
      '/data/organization/logs': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-scheduling': [{ CrewSchedulingOnly: true, CrewSchedulingV1: true }],
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/duty-times': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/crew-compliance': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, ShowCrewCompliancePage: true },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/organization/aerochex': [{ AerochexEnabled: true }],
      '/data/airports': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/settings': true,
      '/data/organization/flight-watch': true,
      '/data/organization/pilot-status': [{ EnableRestAssureIndicator: true }],
      '/data/organization/company-library': [
        { CompanyLibrary: true, CompanyLibraryAdminOnly: false, CompanyLibraryOwnerOnly: false },
      ],
      '/data/organization/custom-forms': [{ EnableCustomForms: true }],
    },
    engineer: {
      '/data/organization/published-schedule': true,
      '/data/aircraft/logs': [
        { CrewSchedulingOnly: false },
        { CrewSchedulingOnly: true, IntegrateLoggingWithCrewScheduleV2: true },
      ],
      '/data/aircraft': true,
      '/data/organization/aircraft': [
        { CrewSchedulingOnly: false, ShowAircraftPage: true },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: true,
        },
      ],
      '/data/organization/fleet-overview': [
        { CrewSchedulingOnly: false, ShowAircraftPage: false },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: false,
        },
      ],
      '/data/organization/logs': true,
      '/data/organization/aerochex': [{ AerochexEnabled: true }],
      '/data/organization/crew-scheduling': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: true, NonAdminCanViewCrewSchedule: true },
      ],
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false, NonAdminCanViewCrewSchedule: true },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false, NonAdminCanViewCrewSchedule: true },
      ],
      '/data/organization/parts': [{ AllowUserPartManagement: true }],
      '/data/organization/parts/:id': [{ AllowUserPartManagement: true }],
      '/data/organization/company-library': [
        {
          CompanyLibrary: true,
          CompanyLibraryAdminOnly: false,
          CompanyLibraryOwnerOnly: false,
          AllowAMEViewCompanyLibrary: true,
        },
      ],
    },
    pilot: {
      '/data/organization/published-schedule': true,
      '/data/aircraft/logs': true,
      '/data/aircraft': true,
      '/data/organization/tracked-items': [{ ManualTrainingItems: true }],
      '/profile/duty': [
        { CrewSchedulingOnly: true, NonAdminCanViewCrewSchedule: false },
        { CrewSchedulingOnly: false },
        { EnableLegacyPilotSchedule: true },
      ],
      '/profile/manage': true,
      '/data/airports': [{ CrewSchedulingOnly: false }],
      '/data/organization/aircraft': [
        { CrewSchedulingOnly: false, ShowAircraftPage: true },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: true,
        },
      ],
      '/data/organization/fleet-overview': [
        { CrewSchedulingOnly: false, ShowAircraftPage: false },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: false,
        },
      ],
      '/data/organization/company-library': [
        { CompanyLibrary: true, CompanyLibraryAdminOnly: false, CompanyLibraryOwnerOnly: false },
      ],
      '/data/organization/crew-scheduling': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: true, NonAdminCanViewCrewSchedule: true },
      ],
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false, NonAdminCanViewCrewSchedule: true },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false, NonAdminCanViewCrewSchedule: true },
      ],
    },
    crewScheduleViewer: {
      '/data/organization/published-schedule': true,
      '/data/aircraft/logs': true,
      '/data/aircraft': true,
      '/profile/duty': [
        { CrewSchedulingOnly: true, NonAdminCanViewCrewSchedule: false },
        { CrewSchedulingOnly: false },
      ],
      '/profile/manage': true,
      '/data/airports': [{ CrewSchedulingOnly: false }],
      '/data/organization/aircraft': [
        { CrewSchedulingOnly: false, ShowAircraftPage: true },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: true,
        },
      ],
      '/data/organization/fleet-overview': [
        { CrewSchedulingOnly: false, ShowAircraftPage: false },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: false,
        },
      ],
      '/data/organization/crew-scheduling': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: true, NonAdminCanViewCrewSchedule: true },
      ],
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false, NonAdminCanViewCrewSchedule: true },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false, NonAdminCanViewCrewSchedule: true },
      ],
    },
    taggedScheduleViewer: {
      '/data/organization/published-schedule': true,
      '/data/aircraft/logs': true,
      '/data/aircraft': true,
      '/profile/duty': [
        { CrewSchedulingOnly: true, NonAdminCanViewCrewSchedule: false },
        { CrewSchedulingOnly: false },
      ],
      '/profile/manage': true,
      '/data/airports': [{ CrewSchedulingOnly: false }],
      '/data/organization/aircraft': [
        { CrewSchedulingOnly: false, ShowAircraftPage: true },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: true,
        },
      ],
      '/data/organization/fleet-overview': [
        { CrewSchedulingOnly: false, ShowAircraftPage: false },
        {
          CrewSchedulingOnly: true,
          IntegrateLoggingWithCrewScheduleV2: true,
          ShowAircraftPage: false,
        },
      ],
      '/data/organization/crew-scheduling': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: true, NonAdminCanViewCrewSchedule: true },
      ],
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false, NonAdminCanViewCrewSchedule: true },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false, NonAdminCanViewCrewSchedule: true },
      ],
    },
    subContractor: {
      '/data/organization/bookings': true,
      '/data/organization/published-schedule': true,
      '/data/organization/crew-scheduling-v3': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
      '/data/organization/crew-scheduling-mobile': [
        { CrewSchedulingOnly: true, CrewSchedulingV1: false },
      ],
    },
    user: {
      '/data/organization/published-schedule': true,
      '/profile/manage': true,
    },
    trackedItemsAdmin: {
      '/data/organization/published-schedule': true,
      '/data/organization/tracked-items': [{ ManualTrainingItems: true }],
      '/data/organization/users': [{ ManualTrainingItems: true }],
      '/data/users': [{ ManualTrainingItems: true }],
    },
    trackedItemsViewer: {
      '/data/organization/published-schedule': true,
      '/data/organization/tracked-items': [{ ManualTrainingItems: true }],
      '/data/organization/users': [{ ManualTrainingItems: true }],
      '/data/users': [{ ManualTrainingItems: true }],
    },
    companyLibraryEditor: {
      '/data/organization/published-schedule': true,
      '/data/organization/company-library': [
        { CompanyLibrary: true, CompanyLibraryAdminOnly: false, CompanyLibraryOwnerOnly: false },
      ],
    },
    companyLibraryViewer: {
      '/data/organization/published-schedule': true,
      '/data/organization/company-library': [
        { CompanyLibrary: true, CompanyLibraryAdminOnly: false, CompanyLibraryOwnerOnly: false },
      ],
    },
    customer: {
      '/data/organization/published-schedule': true,
      '/data/organization/fleet-overview': true,
      '/data/organization/dashboard': true,
      '/profile/manage': false,
    },
    ipadLoginOnly: {
      '/profile/manage': true,
    },
  };

  function safePathLookup(pathname, role, rolePathMap) {
    if (!rolePathMap || typeof rolePathMap !== 'object') {
      console.error('rolePathMap is not a valid object');
      return pathname; // or handle the error appropriately
    }

    const rolePaths = rolePathMap[role];

    if (!rolePaths || typeof rolePaths !== 'object') {
      console.warn(`No rolePathMap entry for role: ${role}`);
      return pathname;
    }

    return pathInPaths(pathname, Object.keys(rolePaths)) || pathname;
  }

  for (let i = 0; i < roles.length; i += 1) {
    const role = roles[i];
    // const matchedPath = pathInPaths(pathname, Object.keys(rolePathMap[role])) || pathname;
    const matchedPath = safePathLookup(pathname, role, rolePathMap);
    // if (rolePathMap[role][matchedPath]) {
    if (rolePathMap && rolePathMap[role] && rolePathMap[role][matchedPath]) {
      if (typeof rolePathMap[role][matchedPath] === 'boolean') {
        if (rolePathMap[role][matchedPath] === true) {
          return true;
        }
        // eslint-disable-next-line no-continue
        continue;
      }

      if (Array.isArray(rolePathMap[role][matchedPath])) {
        const allowedConditions = rolePathMap[role][matchedPath] as {
          CrewSchedulingOnly?: boolean;
          AerochexEnabled?: boolean;
          CrewSchedulingV1?: boolean;
          IntegrateLoggingWithCrewScheduleV2?: boolean;
          EnableRestAssureIndicator?: boolean;
          CompanyLibrary?: boolean;
          EnableCustomForms?: boolean;
          ManualTrainingItems?: boolean;
          LoggingOnly?: boolean;
        }[];

        let isAllowed = [];
        for (let j = 0; j < allowedConditions.length; j += 1) {
          const allowedCondition = allowedConditions[j];
          const restrictions = Object.keys(allowedCondition) as (
            | 'AerochexEnabled'
            | 'CrewSchedulingOnly'
            | 'CrewSchedulingV1'
            | 'IntegrateLoggingWithCrewScheduleV2'
            | 'EnableRestAssureIndicator'
            | 'CompanyLibrary'
            | 'EnableCustomForms'
            | 'ManualTrainingItems'
          )[];

          const pathDetailRestrictions =
            typeof allowedCondition === 'object'
              ? (allowedCondition as {
                  AerochexEnabled?: boolean;
                  CrewSchedulingOnly?: boolean;
                  CrewSchedulingV1?: boolean;
                  IntegrateLoggingWithCrewScheduleV2?: boolean;
                  EnableRestAssureIndicator?: boolean;
                  CompanyLibrary?: boolean;
                  EnableCustomForms?: boolean;
                  ManualTrainingItems?: boolean;
                })
              : {};

          // This loop will see if all the properties in the object are true
          isAllowed.push(true);

          for (let k = 0; k < restrictions.length; k += 1) {
            const restriction = restrictions[k];

            if (pathDetailRestrictions[restriction] !== Boolean(settings[restriction])) {
              isAllowed[j] = false;
              break;
            }
          }
        }

        // if one of the conditions is satisfied, then allow user
        if (isAllowed.some(el => el)) {
          return true;
        }

        // eslint-disable-next-line no-continue
        continue;
      }
    }
  }

  // default to disallow since there are many paths we might not have covered and we want a secure default
  return false;
};
